import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Phone, MessageSquare, Mail, Loader2, Sparkles, Save, Flame,
  User, Home as HomeIcon, DollarSign, Clock, DatabaseZap, Layers,
  Trash2, Send, Bot, CheckCircle2, ScanSearch,
} from "lucide-react";
import SendToPipelineDialog from "@/components/SendToPipelineDialog";
import NativePhoneDialog from "@/components/NativePhoneDialog";
import CallOutcomeDialog from "@/components/CallOutcomeDialog";
import AiComposeDialog from "@/components/AiComposeDialog";

function money(n) {
  if (n == null) return "—";
  return n.toLocaleString(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });
}
function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60)     return `${s}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592e3) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Right-side sheet drawer that shows full lead detail, editable fields,
 * activity timeline (calls + SMS + emails), notes, and an on-demand
 * AI "Next Best Action" suggestion.
 */
export default function LeadDetailDrawer({ leadId, open, onOpenChange, onUpdated }) {
  const { can, user } = useAuth();
  // Owner ask 2026-08-27: only admins can edit/remove existing lead
  // info; anyone on the team can ADD a note. Every existing form
  // control disables when !isAdmin; the new "Add Note" strip stays
  // enabled for everyone via /api/leads/{id}/append-note.
  const isAdmin = !!(user?.is_admin) || user?.role === "admin" || user?.role === "owner" || can("lead.edit");
  const [appendNote, setAppendNote] = useState("");
  const [appendingNote, setAppendingNote] = useState(false);
  const appendNoteAction = async () => {
    if (!appendNote.trim() || !leadId) return;
    setAppendingNote(true);
    try {
      await api.post(`/leads/${leadId}/append-note`, { note: appendNote });
      toast.success("Note added");
      setAppendNote("");
      // Refresh so the appended note shows up in the notes field.
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setAppendingNote(false); }
  };
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activity, setActivity] = useState([]);
  const [emails, setEmails] = useState([]);
  const [nba, setNba] = useState(null);
  const [nbaBusy, setNbaBusy] = useState(false);
  const [altContacts, setAltContacts] = useState(null); // {phones, emails, relatives, mailing_addresses, source}
  const [aiConvo, setAiConvo] = useState(null);  // AI SMS conversation, if any
  const [busyAiKickoff, setBusyAiKickoff] = useState(false);
  // Retell AI voice calls attached to this lead (Phase 1 — inbound only).
  const [retellCalls, setRetellCalls] = useState([]);

  // Editable form state (mirrored from lead)
  const [form, setForm] = useState({});

  const loadLead = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      // No single-lead GET endpoint — list + filter (small user leads volume).
      const [{ data: leads }, hist, mails] = await Promise.all([
        api.get("/leads"),
        api.get("/twilio/history", { params: { lead_id: leadId } }),
        api.get("/emails", { params: { lead_id: leadId } }),
      ]);
      const l = (leads || []).find((x) => x.id === leadId);
      if (!l) { toast.error("Lead not found"); onOpenChange(false); return; }
      setLead(l);
      setForm({
        name: l.name || "",
        phone: l.phone || "",
        email: l.email || "",
        notes: l.notes || "",
        offer_amount: l.offer_amount ?? "",
        payoff_amount: l.payoff_amount ?? "",
        next_action: l.next_action || "",
      });
      setActivity(hist.data || []);
      setEmails(mails.data?.items || []);
      // Also pull alternate contacts (all cached phones/emails/mailing addr/relatives)
      try {
        const { data: alt } = await api.get(`/leads/${leadId}/all-contacts`);
        setAltContacts(alt);
      } catch (_) { setAltContacts(null); }
      // Pull any AI SMS conversation attached to this lead
      try {
        const { data: convo } = await api.get(`/ai-sms/conversations/${leadId}`);
        setAiConvo(convo);
      } catch (_) { setAiConvo(null); }
      // Pull Retell AI voice calls for this lead — feeds the activity timeline.
      try {
        const { data: rc } = await api.get(`/leads/${leadId}/retell-calls`);
        setRetellCalls(rc?.calls || []);
      } catch (_) { setRetellCalls([]); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load lead");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && leadId) {
      setNba(null);
      loadLead();
    }
  }, [open, leadId]);

  const save = async () => {
    if (!leadId) return;
    setSaving(true);
    try {
      const patch = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes,
        next_action: form.next_action || null,
        offer_amount:
          form.offer_amount === "" || form.offer_amount == null
            ? null
            : Number(form.offer_amount),
        payoff_amount:
          form.payoff_amount === "" || form.payoff_amount == null
            ? null
            : Number(form.payoff_amount),
      };
      const { data } = await api.patch(`/leads/${leadId}`, patch);
      setLead(data);
      toast.success("Saved");
      onUpdated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runNba = async () => {
    setNbaBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/nba`);
      setNba(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable");
    } finally { setNbaBusy(false); }
  };

  const [enrichBusy, setEnrichBusy] = useState(false);
  const [pipeOpen, setPipeOpen] = useState(false);
  const [nativeDialog, setNativeDialog] = useState({ open: false, mode: "call" });
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [togglingDnc, setTogglingDnc] = useState(false);
  const [apifyBusy, setApifyBusy] = useState(false);
  const [apifyResult, setApifyResult] = useState(null);
  const [composeDialog, setComposeDialog] = useState({ open: false, channel: "email" });
  const navigate = useNavigate();

  const runApifySkipTrace = async () => {
    if (!leadId) return;
    setApifyBusy(true); setApifyResult(null);
    try {
      const { data } = await api.post("/skip-trace/apify-run", { lead_id: leadId });
      setApifyResult(data);
      const found = (data.phones?.length || 0) + (data.emails?.length || 0);
      // Provider label — strip actor suffix so "apify:jungle_synthesizer/..." shows as just "apify"
      const prov = String(data.provider || "").split(":")[0] || "provider";
      if (found > 0) {
        toast.success(
          `${prov}: found ${data.phones?.length || 0} phone(s), ${data.emails?.length || 0} email(s)`
        );
        loadLead(); onUpdated?.();
      } else {
        toast.info(`${prov} returned no matching contacts for this address`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip trace failed");
    } finally {
      setApifyBusy(false);
    }
  };

  const setTemperature = async (temp) => {
    // Optimistic
    setLead((l) => l ? { ...l, temperature: temp } : l);
    try {
      const { data } = await api.patch(`/leads/${leadId}`, { temperature: temp });
      setLead(data);
      onUpdated?.(data);
      toast.success(`Marked as ${temp.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      loadLead();
    }
  };
  const enrichFromCounty = async () => {
    setEnrichBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/enrich-from-county`);
      if (data.matched) {
        toast.success(`Found: ${data.found?.address || "matched"}`);
        onUpdated?.();
        loadLead();
      } else {
        toast.error(
          `No parcel found for "${data.owner_searched}" in Lee County. `
          + "Try trimming the middle name or searching manually on leepa.org.",
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally { setEnrichBusy(false); }
  };

  const startCall = async () => {
    if (!lead?.phone) return toast.error("No phone number on this lead");
    if (lead.dnc) {
      const ok = window.confirm(
        `${lead.name || "This lead"} is marked Do Not Call${
          lead.dnc_reason ? ` (${lead.dnc_reason})` : ""
        }.\n\nCall anyway?`,
      );
      if (!ok) return;
    }
    // Jump straight into the Twilio browser dialer with this lead pre-selected.
    // Dialer.jsx reads ?leadId=... on mount and auto-dials.
    onOpenChange?.(false);
    navigate(`/dialer?leadId=${leadId}`);
  };

  const toggleDnc = async () => {
    if (!lead) return;
    const next = !lead.dnc;
    setTogglingDnc(true);
    try {
      let reason = null;
      if (next) {
        reason = window.prompt(
          "Optional reason for marking Do Not Call:",
          "Owner asked to not be contacted",
        );
      }
      await api.post(`/leads/${leadId}/dnc`, { dnc: next, reason });
      toast.success(next ? "Marked Do Not Call" : "DNC cleared");
      loadLead();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setTogglingDnc(false);
    }
  };

  const sendText = async () => {
    if (!lead?.phone) return toast.error("No phone number on this lead");
    setComposeDialog({ open: true, channel: "sms" });
  };

  const sendEmail = () => {
    if (!lead?.email) return toast.error("No email address on this lead");
    setComposeDialog({ open: true, channel: "email" });
  };

  const deleteLead = async () => {
    if (!lead) return;
    const label = lead.name || lead.property_address || "this lead";
    if (!window.confirm(
      `Delete "${label}"?\n\nThis removes the lead from your CRM permanently. ` +
      `The underlying property record (if any) is kept.`,
    )) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      onOpenChange(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  // Merge activity + emails, sort by created_at desc
  const timeline = [
    ...activity.map((a) => ({ ...a, _t: "phone" })),
    ...emails.map((e) => ({ ...e, kind: "email", _t: "email" })),
    ...retellCalls.map((c) => ({
      ...c,
      kind: "retell",
      _t: "retell",
      status: c.summary ? "AI Voice · " + (c.outcome || "call") : "AI Voice call",
      body: c.summary,
      direction: c.direction || "inbound",
    })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="lead-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl flex items-center gap-2">
            <span>{loading ? "Loading…" : lead?.name || "Lead"}</span>
            {lead?.dnc && (
              <span
                className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200"
                title={lead.dnc_reason || "Do Not Call"}
                data-testid="lead-dnc-badge"
              >
                DNC
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            {lead?.property_address ? (
              <span className="flex items-center gap-1 text-xs">
                <HomeIcon size={11} /> {lead.property_address}
              </span>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        {loading || !lead ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-6">
            <Loader2 size={14} className="animate-spin" /> Loading lead…
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Prominent "Open in Property Lookup" button — deep-links to
                /lookup?address=... which prefills + auto-runs Analyze.
                Placed at the top of the drawer body so it can't be missed. */}
            {lead?.property_address && (
              <Button
                onClick={() => {
                  onOpenChange?.(false);
                  navigate(`/lookup?address=${encodeURIComponent(lead.property_address)}`);
                }}
                data-testid="lead-open-property-lookup-btn"
                className="w-full rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold"
              >
                <ScanSearch size={16} className="mr-2" />
                Open in Property Lookup
              </Button>
            )}
            {/* Snapshot chips */}
            <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="lead-chips">
              {lead.stage && (
                <span className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200">
                  {lead.stage.replace(/_/g, " ")}
                </span>
              )}
              {lead.motivation_score != null && (
                <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                  <Flame size={9} className="inline -mt-0.5 mr-0.5" />
                  {lead.motivation_score}
                </span>
              )}
              {lead.property_arv != null && (
                <span className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200">
                  ARV {money(lead.property_arv)}
                </span>
              )}
              {lead.property_equity != null && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
                      data-testid="drawer-equity-chip"
                      title={lead.property_equity_pct != null ? `${lead.property_equity_pct}% equity` : "Est Equity"}>
                  Equity {money(lead.property_equity)}
                  {lead.property_equity_pct != null && (
                    <span className="ml-1 text-[9px] opacity-70">({lead.property_equity_pct}%)</span>
                  )}
                </span>
              )}
              {lead.distress_score != null && lead.distress_score !== lead.motivation_score && (
                <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
                      data-testid="drawer-distress-chip"
                      title="Distress Score from list">
                  Distress {lead.distress_score}
                </span>
              )}
              {(lead.property_beds != null || lead.property_baths != null || lead.property_sqft != null) && (
                <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200"
                      data-testid="drawer-bedbath-chip"
                      title="Beds / Baths / SqFt">
                  {lead.property_beds != null ? `${lead.property_beds}bd` : ""}
                  {lead.property_baths != null ? ` / ${lead.property_baths}ba` : ""}
                  {lead.property_sqft != null ? ` · ${Math.round(lead.property_sqft).toLocaleString()} sqft` : ""}
                </span>
              )}
              {lead.property_type && (
                <span className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200"
                      data-testid="drawer-proptype-chip">
                  {lead.property_type}
                </span>
              )}
              {lead.owner_occupied === true && (
                <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                      title="Owner-occupied — seller lives there">
                  Owner-Occ
                </span>
              )}
              {lead.owner_occupied === false && (
                <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-800 border border-violet-200"
                      title="Absentee owner">
                  Absentee
                </span>
              )}
              {lead.listed_for_sale === true && (
                <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
                      title="Currently on MLS">
                  On MLS
                </span>
              )}
              {lead.listed_fsbo === true && (
                <span className="px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-800 border border-yellow-200"
                      title="For Sale By Owner">
                  FSBO
                </span>
              )}
              {(lead.payoff_amount ?? lead.mortgage_balance) != null && (
                <span className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200"
                      title={lead.payoff_amount != null ? "Confirmed payoff" : "Estimate"}
                      data-testid="drawer-payoff-chip">
                  Payoff {money(lead.payoff_amount ?? lead.mortgage_balance)}
                  {lead.payoff_amount == null && <span className="ml-1 text-[9px] text-muted-foreground">(est)</span>}
                </span>
              )}
              {lead.property_mao != null && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  MAO {money(lead.property_mao)}
                </span>
              )}
              {lead.source_tag && (
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                  {lead.source_tag.replace(/_/g, " ")}
                </span>
              )}
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-3 gap-2">
              <Button variant="secondary" onClick={startCall}
                      disabled={!lead.phone}
                      className="rounded-full text-xs h-9"
                      data-testid="drawer-call-btn">
                <Phone size={12} className="mr-1" /> Call
              </Button>
              <Button variant="secondary" disabled={!lead.phone}
                      onClick={sendText}
                      className="rounded-full text-xs h-9"
                      data-testid="drawer-sms-btn">
                <MessageSquare size={12} className="mr-1" /> Text
              </Button>
              <Button variant="secondary" disabled={!lead.email}
                      onClick={sendEmail}
                      className="rounded-full text-xs h-9"
                      data-testid="drawer-email-btn">
                <Mail size={12} className="mr-1" /> Email
              </Button>
            </div>

            {/* DNC toggle */}
            <button
              onClick={toggleDnc}
              disabled={togglingDnc}
              className={`w-full text-xs h-8 rounded-full border transition-colors ${
                lead.dnc
                  ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                  : "border-[var(--app-line)] text-muted-foreground hover:bg-[var(--app-surface)]"
              }`}
              data-testid="drawer-dnc-toggle"
            >
              {lead.dnc
                ? "✓ Marked Do Not Call — click to unmute"
                : "Mark as Do Not Call"}
            </button>

            {/* Temperature classification */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                Lead temperature
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="drawer-temperature">
                {[
                  { key: "cold", label: "Cold",
                    active: "bg-sky-100 text-sky-900 border-sky-300",
                    inactive: "bg-white text-sky-700 border-sky-200 hover:bg-sky-50" },
                  { key: "warm", label: "Warm",
                    active: "bg-amber-100 text-amber-900 border-amber-400",
                    inactive: "bg-white text-amber-700 border-amber-200 hover:bg-amber-50" },
                  { key: "hot", label: "Hot",
                    active: "bg-red-100 text-red-900 border-red-400",
                    inactive: "bg-white text-red-700 border-red-200 hover:bg-red-50" },
                ].map((t) => {
                  const on = lead.temperature === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTemperature(t.key)}
                      data-testid={`drawer-temp-${t.key}`}
                      className={`text-xs font-semibold py-2 rounded-md border transition-colors ${
                        on ? t.active : t.inactive
                      }`}
                    >
                      {t.key === "hot" && "🔥 "}
                      {t.key === "warm" && "☀ "}
                      {t.key === "cold" && "❄ "}
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Send to pipeline */}
            <Button
              onClick={() => setPipeOpen(true)}
              variant="secondary"
              className="w-full rounded-full text-xs h-9"
              data-testid="drawer-send-pipeline-btn"
            >
              <Layers size={12} className="mr-1" /> Send to pipeline
              <span className="text-muted-foreground ml-2 text-[10px]">
                Wholesale · Acquisitions · Flips · …
              </span>
            </Button>

            {/* AI Next Best Action */}
            {(lead?.property_address || "").startsWith("Owner:") && (
              <div className="rounded-lg border border-sky-300 bg-sky-50 p-3"
                   data-testid="drawer-enrich-card">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <DatabaseZap size={12} className="text-sky-700" />
                  Court record — no address yet
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  This lead came from a LIS PENDENS / probate court export. Look it
                  up in the county parcel databases to fill in the address,
                  sqft, ARV and absentee flag.
                </div>
                <Button size="sm" onClick={enrichFromCounty}
                        disabled={enrichBusy}
                        className="mt-2 rounded-full bg-sky-600 text-white hover:bg-sky-700 h-7 text-[11px]"
                        data-testid="drawer-enrich-btn">
                  {enrichBusy
                    ? <><Loader2 size={11} className="animate-spin mr-1" /> Looking up…</>
                    : <>Look up address (all counties)</>}
                </Button>
              </div>
            )}

            {/* Apify skip-trace — hidden until the lead has a real street
                address (not just a court case number). Without an address
                the API will match on name-only and return wrong contacts. */}
            {!lead?.phone && (lead?.property_address || "").trim().length >= 8 && (
              <div className="rounded-lg border border-purple-300 bg-purple-50 p-3"
                   data-testid="drawer-apify-card">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <Sparkles size={12} className="text-purple-700" />
                  Skip Trace
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Run the configured skip-trace provider to pull phone, email
                  and relatives for this owner + address. Result caches to your
                  Skip Trace library so re-runs are free.
                </div>
                <Button size="sm" onClick={runApifySkipTrace}
                        disabled={apifyBusy}
                        className="mt-2 rounded-full bg-purple-600 text-white hover:bg-purple-700 h-7 text-[11px]"
                        data-testid="drawer-apify-btn">
                  {apifyBusy
                    ? <><Loader2 size={11} className="animate-spin mr-1" /> Running Actor…</>
                    : <>Skip trace this lead</>}
                </Button>
                {apifyResult && (
                  <div className="mt-2 text-[11px] rounded bg-white border border-purple-200 p-2"
                       data-testid="drawer-apify-result">
                    <div><strong>Phones:</strong> {(apifyResult.phones || []).join(", ") || "—"}</div>
                    <div><strong>Emails:</strong> {(apifyResult.emails || []).join(", ") || "—"}</div>
                    {apifyResult.relatives?.length > 0 && (
                      <div><strong>Relatives:</strong> {apifyResult.relatives.join(", ")}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="rounded-lg border border-[var(--app-line)] bg-gradient-to-br from-violet-50 to-white p-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <Sparkles size={12} className="text-violet-600" />
                  AI Next Best Action
                </div>
                <Button size="sm" variant="ghost" onClick={runNba}
                        disabled={nbaBusy}
                        className="rounded-full text-[11px] h-7"
                        data-testid="drawer-nba-btn">
                  {nbaBusy ? <Loader2 size={11} className="animate-spin" />
                            : (nba ? "Refresh" : "Suggest")}
                </Button>
              </div>
              {nba ? (
                <div className="mt-2 text-xs space-y-1" data-testid="drawer-nba-result">
                  <div className="font-medium">{nba.action}</div>
                  {nba.rationale && (
                    <div className="text-muted-foreground">{nba.rationale}</div>
                  )}
                  {nba.script && (
                    <div className="mt-2 p-2 bg-white border rounded text-[11px] font-mono whitespace-pre-wrap">
                      {nba.script}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Tap Suggest to have GPT read this lead and recommend the next move.
                </div>
              )}
            </div>

            {/* Editable fields */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         className="mt-1" data-testid="drawer-field-name" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone}
                         onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         placeholder="+13055551234"
                         className="mt-1" data-testid="drawer-field-phone" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="mt-1" data-testid="drawer-field-email" />
              </div>

              {/* Alternate contacts panel — powered by /leads/{id}/all-contacts.
                  Shows every phone/email/mailing addr/relative REAPI ever
                  returned for this address, not just the primary. */}
              {altContacts && (
                (altContacts.phones?.length > 1 ||
                 altContacts.emails?.length > 1 ||
                 altContacts.mailing_addresses?.length > 0 ||
                 altContacts.relatives?.length > 0) && (
                <div
                  className="rounded-xl border border-violet-200 bg-violet-50 p-3"
                  data-testid="drawer-alt-contacts"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-violet-800 font-semibold">
                      Alternate contacts · skip-trace
                    </div>
                    {altContacts.source && (
                      <div className="text-[9px] uppercase tracking-widest text-violet-600">
                        {String(altContacts.source).replace(/_/g, " ")}
                      </div>
                    )}
                  </div>

                  {altContacts.phones?.length > 1 && (
                    <div className="mt-2" data-testid="drawer-alt-phones">
                      <div className="text-[10px] text-violet-700 mb-1">
                        Other phones ({altContacts.phones.length - 1})
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {altContacts.phones
                          .filter((p) => p && p !== altContacts.primary_phone)
                          .slice(0, 8)
                          .map((p, i) => (
                          <button
                            key={i}
                            onClick={() => setForm({ ...form, phone: p })}
                            data-testid={`drawer-alt-phone-${i}`}
                            className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-white px-2.5 py-1 text-[11px] font-mono text-violet-900 hover:bg-violet-100 transition-colors"
                            title={`Use ${p} as primary phone`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {altContacts.emails?.length > 1 && (
                    <div className="mt-2" data-testid="drawer-alt-emails">
                      <div className="text-[10px] text-violet-700 mb-1">
                        Other emails ({altContacts.emails.length - 1})
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {altContacts.emails
                          .filter((e) => e && e !== altContacts.primary_email)
                          .slice(0, 8)
                          .map((e, i) => (
                          <button
                            key={i}
                            onClick={() => setForm({ ...form, email: e })}
                            data-testid={`drawer-alt-email-${i}`}
                            className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-white px-2.5 py-1 text-[11px] text-violet-900 hover:bg-violet-100 transition-colors"
                            title={`Use ${e} as primary email`}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {altContacts.mailing_addresses?.length > 0 && (
                    <div className="mt-2" data-testid="drawer-alt-mailing">
                      <div className="text-[10px] text-violet-700 mb-1">
                        Owner mailing address(es) — where the deed statements go
                      </div>
                      <ul className="space-y-0.5 text-[11px] text-violet-900">
                        {altContacts.mailing_addresses.slice(0, 3).map((m, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="w-1 h-1 mt-1.5 rounded-full bg-violet-500 shrink-0" />
                            <span>{m}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {altContacts.relatives?.length > 0 && (
                    <div className="mt-2" data-testid="drawer-alt-relatives">
                      <div className="text-[10px] text-violet-700 mb-1">
                        Known relatives / associated persons
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {altContacts.relatives.slice(0, 6).map((r, i) => (
                          <span
                            key={i}
                            className="rounded-full border border-violet-300 bg-white px-2.5 py-1 text-[11px] text-violet-900"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-2 text-[10px] text-violet-700/80 leading-snug">
                    Click any chip to promote it to the primary field. Absentee
                    owners often list a phone in a different state than the
                    property — that&apos;s normal, not a bad match.
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Offer amount</Label>
                  <Input type="number" value={form.offer_amount}
                         onChange={(e) => setForm({ ...form, offer_amount: e.target.value })}
                         placeholder="e.g. 185000"
                         disabled={!isAdmin}
                         className="mt-1" data-testid="drawer-field-offer" />
                </div>
                <div>
                  <Label className="text-xs">Payoff amount</Label>
                  <Input type="number" value={form.payoff_amount}
                         onChange={(e) => setForm({ ...form, payoff_amount: e.target.value })}
                         placeholder={
                           lead?.mortgage_balance != null
                             ? `Est. ${money(lead.mortgage_balance)}`
                             : "e.g. 152000"
                         }
                         disabled={!isAdmin}
                         className="mt-1" data-testid="drawer-field-payoff" />
                </div>
              </div>
              {(() => {
                const offer = form.offer_amount === "" ? null : Number(form.offer_amount);
                const payoff = form.payoff_amount === ""
                  ? (lead?.mortgage_balance ?? null)
                  : Number(form.payoff_amount);
                if (offer == null || payoff == null || Number.isNaN(offer) || Number.isNaN(payoff)) return null;
                const cashOut = offer - payoff;
                return (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center justify-between text-xs"
                       data-testid="drawer-cashout-chip">
                    <span className="text-emerald-800">
                      Est. seller cash-out
                    </span>
                    <span className="font-mono font-bold text-emerald-900">
                      {money(cashOut)}
                    </span>
                  </div>
                );
              })()}
              <div>
                <Label className="text-xs">Next action</Label>
                <Input value={form.next_action}
                       onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                       placeholder="e.g. Call back Tuesday"
                       disabled={!isAdmin}
                       className="mt-1" data-testid="drawer-field-next" />
              </div>
              <div>
                <Label className="text-xs">Notes {isAdmin ? "" : "(read-only for team members)"}</Label>
                <Textarea rows={4} value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          className="mt-1"
                          placeholder="Anything the next teammate should know…"
                          disabled={!isAdmin}
                          data-testid="drawer-field-notes" />
              </div>

              {/* Always-available "Add Note" — anyone on the team can
                  append without triggering an admin-only edit. Appends
                  a timestamp+name-prefixed line to the notes field via
                  POST /leads/{id}/append-note. */}
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3"
                   data-testid="drawer-add-note-strip">
                <Label className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
                  Add a note (available to everyone)
                </Label>
                <Textarea rows={2} value={appendNote}
                          onChange={(e) => setAppendNote(e.target.value)}
                          className="mt-1 bg-white"
                          placeholder="Quick note — appended to the record, does not overwrite existing notes."
                          data-testid="drawer-append-note-input" />
                <Button
                  onClick={appendNoteAction}
                  disabled={appendingNote || !appendNote.trim()}
                  size="sm"
                  className="mt-2 rounded-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="drawer-append-note-btn"
                >
                  {appendingNote
                    ? <Loader2 size={12} className="animate-spin mr-1.5" />
                    : <Save size={12} className="mr-1.5" />}
                  Add note
                </Button>
              </div>

              {isAdmin && (
                <Button onClick={save} disabled={saving}
                        className="w-full rounded-full"
                        data-testid="drawer-save-btn">
                  {saving ? <Loader2 size={13} className="animate-spin mr-2" />
                          : <Save size={13} className="mr-2" />}
                  Save changes
                </Button>
              )}
            </div>

            {/* AI SMS Agent — full transcript + extracted signals */}
            {aiConvo ? (
              <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3"
                   data-testid="drawer-ai-sms">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-violet-700 font-semibold">
                    <Bot size={13} />
                    AI SMS Agent
                    {aiConvo.handoff_at && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] normal-case px-2 py-0.5 rounded-full bg-violet-600 text-white">
                        <CheckCircle2 size={10} /> Qualified — {aiConvo.handoff_reason?.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-violet-700 font-mono">
                    Score {aiConvo.qualification_score ?? 0}/100
                  </div>
                </div>

                {/* Extracted qualification signals */}
                {aiConvo.extracted && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mb-2 text-[11px]">
                    {[
                      ["Owner",        aiConvo.extracted.is_owner === null ? null : (aiConvo.extracted.is_owner ? "Yes" : "No")],
                      ["Motivation",   aiConvo.extracted.motivation_reason],
                      ["Timeline",     aiConvo.extracted.timeline_bucket?.replace(/_/g, " ")],
                      ["Condition",    aiConvo.extracted.condition],
                      ["Asking",       aiConvo.extracted.asking_price != null ? `$${Number(aiConvo.extracted.asking_price).toLocaleString()}` : null],
                      ["Mortgage",     aiConvo.extracted.mortgage_balance != null ? `$${Number(aiConvo.extracted.mortgage_balance).toLocaleString()}` : null],
                      ["Other offers", aiConvo.extracted.other_offers === null ? null : (aiConvo.extracted.other_offers ? "Yes" : "No")],
                    ].map(([label, val]) => (
                      <div key={label} className="bg-white/70 rounded-md px-2 py-1 border border-violet-100">
                        <div className="text-[9px] uppercase tracking-widest text-violet-500">{label}</div>
                        <div className={val ? "text-slate-800 font-medium" : "text-slate-400 italic"}>
                          {val || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Transcript */}
                <div className="max-h-64 overflow-y-auto space-y-1 pr-1"
                     data-testid="drawer-ai-transcript">
                  {(aiConvo.messages || []).map((m, i) => {
                    const isSeller = m.role === "inbound";
                    return (
                      <div key={i}
                           className={`flex ${isSeller ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[12px] ${
                          isSeller
                            ? "bg-white border border-slate-200 text-slate-800"
                            : "bg-violet-600 text-white"
                        }`}>
                          <div className="text-[9px] uppercase tracking-widest opacity-70 mb-0.5">
                            {isSeller ? "Seller" : "Alex (AI)"} · {relativeTime(m.at)}
                          </div>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* No AI conversation yet — offer to start one */
              lead.phone && (
                <div className="rounded-xl border border-dashed border-violet-200 bg-violet-50/40 p-3 flex items-center gap-3"
                     data-testid="drawer-ai-sms-start">
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-violet-700 font-semibold flex items-center gap-1.5">
                      <Bot size={12} /> AI SMS Agent
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Have Alex send a personalized opening text and take the seller through qualification.
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={busyAiKickoff}
                    onClick={async () => {
                      setBusyAiKickoff(true);
                      try {
                        await api.post(`/ai-sms/kickoff/${lead.id}`, {});
                        toast.success("AI outreach sent — refreshing…");
                        const { data: convo } = await api.get(`/ai-sms/conversations/${lead.id}`);
                        setAiConvo(convo);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Couldn't start AI outreach");
                      } finally {
                        setBusyAiKickoff(false);
                      }
                    }}
                    className="rounded-full bg-violet-600 hover:bg-violet-700 text-white text-[11px] uppercase tracking-widest px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid="drawer-ai-kickoff-btn"
                  >
                    {busyAiKickoff
                      ? <><Loader2 size={11} className="animate-spin" /> Sending…</>
                      : <><Send size={11} /> Start AI outreach</>}
                  </button>
                </div>
              )
            )}

            {/* Activity timeline */}
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                <Clock size={11} /> Activity
              </div>
              {timeline.length === 0 ? (
                <div className="text-xs text-muted-foreground border border-dashed border-[var(--app-line)] rounded p-3 text-center"
                     data-testid="drawer-timeline-empty">
                  No activity yet — call, text, or email to start the trail.
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="drawer-timeline">
                  {timeline.slice(0, 20).map((row, i) => (
                    <div key={i}
                         className="flex items-start gap-2 text-xs border-l-2 border-[var(--app-line)] pl-2 py-1">
                      <span className="mt-0.5">
                        {row.kind === "call"  && <Phone size={11} className="text-blue-600" />}
                        {row.kind === "sms"   && <MessageSquare size={11} className="text-emerald-600" />}
                        {row.kind === "email" && <Mail size={11} className="text-violet-600" />}
                        {row.kind === "retell" && <Phone size={11} className="text-purple-600" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {row.kind === "call"  && (row.status || "Call")}
                          {row.kind === "sms"   && (row.body ? row.body.slice(0, 80) : "SMS")}
                          {row.kind === "email" && (row.subject || "Email")}
                          {row.kind === "retell" && (
                            <span className="flex items-center gap-1">
                              <span className="text-[9px] uppercase tracking-widest px-1 rounded bg-purple-100 text-purple-700">AI</span>
                              {row.body ? row.body.slice(0, 80) : (row.status || "AI Voice call")}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {relativeTime(row.created_at)}
                          {row.direction && <> · {row.direction}</>}
                          {row.kind === "retell" && row.recording_url && (
                            <> · <a href={row.recording_url} target="_blank" rel="noreferrer" className="text-purple-700 underline">recording</a></>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Danger zone — delete lead (gated by lead.delete cap) */}
            {can?.("lead.delete") && (
              <div className="pt-4 mt-4 border-t border-dashed border-red-200"
                   data-testid="drawer-danger-zone">
                <div className="text-[10px] uppercase tracking-widest text-red-600 mb-2">
                  Danger zone
                </div>
                <Button
                  variant="outline"
                  onClick={deleteLead}
                  disabled={deleting}
                  className="w-full rounded-full text-xs h-9 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="drawer-delete-btn"
                >
                  {deleting ? (
                    <><Loader2 size={12} className="animate-spin mr-1.5" /> Deleting…</>
                  ) : (
                    <><Trash2 size={12} className="mr-1.5" /> Delete lead (duplicate / bad data)</>
                  )}
                </Button>
                <div className="text-[10px] text-muted-foreground mt-1.5 text-center">
                  Removes the lead from your CRM permanently. The underlying property record stays.
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
      <SendToPipelineDialog
        open={pipeOpen}
        onOpenChange={setPipeOpen}
        leadId={leadId}
        currentBoardId={lead?.board_id}
        currentColumnKey={lead?.column_key}
        onMoved={() => { onUpdated?.(); loadLead(); }}
      />
      <NativePhoneDialog
        open={nativeDialog.open}
        onOpenChange={(v) => setNativeDialog((s) => ({ ...s, open: v }))}
        phone={lead?.phone}
        name={lead?.name}
        mode={nativeDialog.mode}
        leadId={leadId}
        dnc={!!lead?.dnc}
        onCallOutcome={() => setOutcomeOpen(true)}
      />
      <CallOutcomeDialog
        open={outcomeOpen}
        onOpenChange={setOutcomeOpen}
        leadId={leadId}
        onLogged={() => { loadLead(); onUpdated?.(); }}
      />
      <AiComposeDialog
        open={composeDialog.open}
        onOpenChange={(v) => setComposeDialog((s) => ({ ...s, open: v }))}
        channel={composeDialog.channel}
        lead={lead}
      />
    </Sheet>
  );
}
